import type En from '@/common/classes/En.ts'
import type { SwingTilesRowScopeLike } from '@/common/utils/tilesRowPanelData'

export interface JsResultProperties {
  isContext: boolean
  _success: boolean
  cleanup_data: boolean
  close__after__complete: boolean
  return__from__dialog: boolean
  retry__operation: boolean
  dispatched_visibilities?: any
  dispatched_editables?: any
}

// Контекст action, его свойства (из формы) и текущее состояние
export interface ActionContext {
  /** Идентификатор action */
  id: string
  /** Название действия */
  name: string
  /** Идентификатор элемента, к которому привязано действие */
  elementId?: string
  /** Идентификатор панели, к которой привязано действие */
  panelId?: string
  /** Идентификатор формы, к которой привязано действие */
  formId?: string
  /** Идентификатор страницы, к которой привязано действие */
  pageId?: string
  /** Уровень действия: форма, панель, элемент или страница */
  level: 'form' | 'panel' | 'element' | 'page'
  /** Данные действия (свойства из формы и текущее состояние) */
  data: any
  /** Платформа, на которой выполняется действие */
  platform?: string
  /** Данные выбранной строки для действий в контексте таблицы */
  selectedData?: any
  /** Только selectedData в clientData / en (inline, events), без всей формы */
  limitToSelectedData?: boolean
  /** Флаг выполнения действия для связанного окна */
  doActionForAdjacentWindow?: boolean
  /** Тип окна для открытия (dialog, window, document и т.д.) */
  windowType?: string
  /** Как в Swing: SAME_WINDOW + Alt+клик → выполнять как NEW_WINDOW */
  openInNewWindowFromModifier?: boolean
  /** JavaScript код, выполняемый при клике на действие */
  onClick?: string
  /** Текст подтверждения перед выполнением действия */
  confirm?: string
  /** Идентификатор действия для передачи данных */
  transfer?: string
  /** Тип передачи данных (document, call_chain и т.д.) */
  transferType?: string
  /** Идентификатор родительской страницы */
  parentPageId?: string
  /** События действия */
  event?: {
    /** Код, выполняемый при закрытии диалога */
    dialogClosed?: string
    /** Код, выполняемый при вызове цепочки действий */
    chainCalled?: string
  }
  /** Идентификатор потока (flow) */
  flow?: string
  /** Дополнительные параметры действия */
  params?: any
  /** Контекст строки Tiles для putPanelData / getForm (WUI-373) */
  tilesRowScope?: SwingTilesRowScopeLike | null
  /** Слот pagesData FormProvider (legacy GlobalProvider.elementAction). */
  pageDataSlot?: string
  /** Плоские поля формы для elementAction (legacy GlobalProvider). */
  form?: Record<string, unknown>
  actionItem?: Record<string, unknown>
  pageData?: Record<string, unknown>
  dialogConfig?: Record<string, unknown>
  controls?: Record<string, unknown>
  /**
   * En хост-скрипта при вложенном doAction: UniData после setField/refill в том же onClick,
   */
  nestedHostEn?: En
  middleScriptUniData?: ExchangeData
}

/**
 * Интерфейс данных обмена между клиентом и мидл-сервером
 * Отправляется в теле запроса под именем currentData
 * Принимается в теле ответа
 * jsResultProperties — флаги после клиентского скрипта (En); с middle приходят в JSResult.properties
 * TODO: интерфейс надо перенести в какие-то общие типы, добавить его в core.service, например createSafeClientData
 */
export interface ExchangeData {
  clientData?: any
  fields?: any
  jsResultProperties?: JsResultProperties
  formError?: any // взято из createSafeClientData core.service
  exception?: any // взято из createSafeClientData core.service
}


export interface StateData {
  elementId?: boolean
}

/**
 * Зависимости для выполнения action: store, роутер, коллбэки обновления UI и доступа к данным.
 * Передаются в action service при вызове, позволяют подставлять контекст (например, из iframe/платформы).
 */
export interface ActionDependencies {
  /** Store приложения: формы, страницы, диалоги, текущая платформа, навигация по формам */
  appStore?: any
  /** Роутер Vue для программной навигации (переход на страницу платформы, документа и т.д.) */
  router?: any
  /** Сервис или карта действий (выполнение других action по имени/контексту) */
  actions?: any
  /** API диалогов (открытие/закрытие, результат) */
  dialog?: any
  /** Перезагрузка элемента по имени с новыми данными и опциональным контекстом action */
  reloadElement?: (elementName: string, data: any, actionContext?: any) => Promise<any>
  /** Обновление данных элемента по имени (refill полей без полной перезагрузки) */
  refillElement?: (elementName: string, fieldData: any, formId?: string) => void
  /** Получение данных формы по formId/panelId/elementId, с опцией flatten */
  getForm?: (params: {
    formId?: string | number
    panelId?: string | number
    elementId?: string | number
    pageDataSlot?: string
    flatten?: boolean
    tilesRowScope?: SwingTilesRowScopeLike | null
  }) => any
  /** Получение данных страницы по formId/pageId, с опцией flatten */
  getPageData?: (params: {
    formId?: string | number
    pageId?: string | number
    flatten?: boolean
  }) => any
  /** Получение checkbox selection для конкретной таблицы/action context */
  getTableSelection?: (
    formId?: string | number,
    panelId?: string | number,
    elementId?: string | number
  ) => { fieldName: string; rows: any[] } | null
  /** Получение всех checkbox selections для формы/панели */
  getTableSelections?: (
    formId?: string | number,
    panelId?: string | number
  ) => Array<{ fieldName: string; rows: any[] }>
  /** Выполнение JavaScript-кода из конфигурации действия (onClick и т.д.) */
  runCode?: (cmd: string, selectedData?: any) => void
  /** Установка и отображение ошибок на странице (setErrors) */
  pageErrors?: any
  /** Обработка состояний формы (processStates после ответа сервера) */
  formStates?: any
  /** Открытие URL из ответа (open_urls) в новой вкладке/окне */
  openUrls?: (URL?: any) => void
  /** Отображение алертов/уведомлений из ответа (displayAlerts) */
  alerts?: any
  /** Провайдер формы: прокрутка к элементу (scrollToElement) и др. */
  formProvider?: any
  /** Данные текущей страницы (кэш/доступ к pageData) */
  pageData?: any
  /** Ключ текущего слота в appStore.pagesData */
  pageDataSlot?: string
  /** Runtime form id для реестра / refill / действий */
  pageRegistryFormId?: string
  /** Dialog/window id */
  pageWindowInstanceId?: string
  /** Получение данных элементов (getElements) */
  getElements?: () => any
}

/** Создаёт копию пустых данных обмена (для избежания мутации) */
export function createEmptyExchangeData(): ExchangeData {
  return {
    clientData: {
      doActionId: 0,
      elementConditions: null,
      openPage: 0,
      params: {},
      reloadActions: null,
    },
    fields: {
      fields: {},
      fieldAttributes: {},
      errors: {},
      attributes: {},
    },
  }
}

/** Начальные значения ExchangeData */
export const INITIAL_EXCHANGE_DATA: ExchangeData = {
  clientData: {
    doActionId: 0,
    elementConditions: null,
    openPage: 0,
    params: {},
    reloadActions: null,
  },
  fields: {
    fields: {},
    fieldAttributes: {},
    errors: {},
    attributes: {},
  },
}

/** Начальные значения JsResultProperties */
export const INITIAL_JS_RESULT_PROPERTIES: JsResultProperties = {
  isContext: false,
  _success: false,
  cleanup_data: false,
  close__after__complete: false,
  return__from__dialog: false,
  retry__operation: false,
}

/** Таймаут выполнения onClick на сервере (мс) */
export const SCRIPT_EXECUTION_TIMEOUT_MS = 300000

/** Transfer types для document */
export const DOCUMENT_TRANSFER_TYPES = [
  'document',
  'call_chain_change_doc',
  'call_chain_conditional_change_doc',
  'context_call_change_doc',
  'context_call_conditional_change_doc',
] as const

/** Return transfer type codes */
export const RETURN_TRANSFER_TYPE_CODES = ['5', '6', '7', '8', '9', '10', '11'] as const
